import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { formatDuration, formatNumber, getBpoVendor, ratio } from './format';
import LevelBadge from './LevelBadge';

/**
 * Kuyruk adları hizmeti veren BPO firmasıyla bitiyor (_ASSISTT, _MEGA, _CMC).
 * Mutabakatın asıl kırılımı bu olduğu için kuyruk listesinden türetip ayrı
 * gösteriyoruz — ek bir sorgu gerekmiyor.
 */
export const groupByVendor = (queues = []) => {
  const totals = {};

  queues.forEach((q) => {
    const vendor = getBpoVendor(q.queue);
    const row =
      totals[vendor] ||
      (totals[vendor] = {
        vendor,
        queueCount: 0,
        incoming: 0,
        answered: 0,
        abandoned: 0,
        slWeight: 0,
        ahtWeight: 0
      });
    row.queueCount += 1;
    row.incoming += q.incoming;
    row.answered += q.answered;
    row.abandoned += q.abandoned;
    row.slWeight += q.sl * q.incoming;
    row.ahtWeight += q.aht * q.answered;
  });

  return Object.values(totals)
    .map((row) => ({
      ...row,
      sl: row.incoming ? row.slWeight / row.incoming : null,
      aht: row.answered ? row.ahtWeight / row.answered : null
    }))
    .sort((a, b) => b.incoming - a.incoming);
};

const BpoBreakdown = ({ queues, loading }) => {
  const rows = useMemo(() => groupByVendor(queues), [queues]);

  if (loading) {
    return (
      <Box sx={{ display: 'grid', gap: 1.5 }}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} height={30} />
        ))}
      </Box>
    );
  }

  const largest = Math.max(...rows.map((r) => r.incoming), 1);

  return (
    <Box>
      {rows.map((row) => (
        <Box
          key={row.vendor}
          sx={(theme) => ({
            display: 'grid',
            gridTemplateColumns: 'minmax(96px,1.1fr) minmax(56px,2fr) 58px 68px 74px',
            gap: 1.25,
            alignItems: 'center',
            py: 1.25,
            borderBottom: `1px solid ${theme.palette.divider}`,
            '&:last-of-type': { borderBottom: 0 }
          })}
        >
          <Box>
            <Typography component="span" sx={{ fontSize: '0.82rem', fontWeight: 500 }}>
              {row.vendor}
            </Typography>
            <Typography component="span" sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
              {' · '}
              {row.queueCount} kuyruk
            </Typography>
          </Box>

          <Box
            sx={(theme) => ({
              height: 8,
              borderRadius: 4,
              backgroundColor:
                theme.palette.mode === 'dark' ? 'rgba(255,255,255,.07)' : theme.palette.grey[200],
              overflow: 'hidden'
            })}
          >
            <Box
              sx={{
                height: '100%',
                width: `${ratio(row.incoming, largest) || 0}%`,
                borderRadius: 4,
                backgroundColor: 'primary.main'
              }}
            />
          </Box>

          <Typography
            sx={{ textAlign: 'right', fontSize: '0.8rem', fontVariantNumeric: 'tabular-nums' }}
          >
            {formatNumber(row.incoming)}
          </Typography>
          <Box sx={{ textAlign: 'right' }}>
            <LevelBadge value={row.sl} />
          </Box>
          <Typography
            sx={{
              textAlign: 'right',
              fontSize: '0.75rem',
              color: 'text.secondary',
              fontVariantNumeric: 'tabular-nums'
            }}
          >
            {formatDuration(row.aht)} AHT
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

BpoBreakdown.propTypes = {
  queues: PropTypes.array,
  loading: PropTypes.bool
};

export default BpoBreakdown;
